import React, { useState } from 'react';

// Derive a Pokemon TCG CDN URL from a tcg_card_id like "swsh4-25" or "sv3pt5-150".
// API convention: https://images.pokemontcg.io/<setId>/<number>.png (and _hires.png).
function tcgImageFor(tcgCardId, hires) {
  if (!tcgCardId || typeof tcgCardId !== 'string') return null;
  const dash = tcgCardId.lastIndexOf('-');
  if (dash <= 0) return null;
  const setId = tcgCardId.slice(0, dash);
  const num = tcgCardId.slice(dash + 1);
  if (!setId || !num) return null;
  return `https://images.pokemontcg.io/${setId}/${num}${hires ? '_hires' : ''}.png`;
}

/**
 * Renders the best-available product image:
 *   1. product.image_url (admin-uploaded or enriched)
 *   2. Pokemon TCG CDN derived from product.tcg_card_id
 *   3. neutral placeholder (no broken-image icon)
 *
 * If primary errors at runtime we fall through to TCG, then placeholder.
 */
// Sealed products (tins, boxes, ETBs, collections, poke balls, portfolios…)
// must never fall back to single-card CDN art — that art is for an individual
// card and looks wrong on a sealed product tile.
const SEALED_HINT = /\b(tin|box|booster|bundle|elite trainer|etb|collection|collector|blister|deck|build ?& ?battle|premium|portfolio|binder|case|chest|pack|poke ?ball|world championship)\b/i;
function looksSealed(product) {
  return product?.condition === 'Sealed' || SEALED_HINT.test(product?.name || '');
}

export default function ProductImage({ product, alt, className, hires = false }) {
  const primary  = product?.image_url || null;
  // Suppress the TCG-card fallback for sealed products (see SEALED_HINT).
  const tcgUrl   = looksSealed(product) ? null : tcgImageFor(product?.tcg_card_id, hires);
  const [stage, setStage] = useState(primary ? 0 : tcgUrl ? 1 : 2);

  if (stage === 0 && primary) {
    // Use plain <img> instead of ResponsiveImage so we get onError, which
    // automatically falls through to the TCG CDN guess or the placeholder
    // when the primary URL is broken / blocked / hotlink-protected.
    return (
      <img
        src={primary}
        alt={alt || product?.name || ''}
        className={className}
        loading="lazy"
        onError={() => setStage(tcgUrl ? 1 : 2)}
      />
    );
  }
  if (stage === 1 && tcgUrl) {
    return (
      <img
        src={tcgUrl}
        alt={alt || product?.name || ''}
        className={className}
        loading="lazy"
        onError={() => setStage(2)}
      />
    );
  }
  return (
    <div className={`product-image-placeholder ${className || ''}`} aria-label="No image available">
      <span>No image</span>
    </div>
  );
}

// Allow callers (e.g. cart, drawer) to pre-resolve a URL synchronously.
export function resolveProductImage(product) {
  if (!product) return null;
  return product.image_url || tcgImageFor(product.tcg_card_id) || null;
}
